import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon, 
  UserIcon, 
  UsersIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  ClockIcon,
  TrashIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import useCourseUsers from '../hooks/useCourseUsers';
import useUserEnrollment from '../hooks/useUserEnrollment';
import { parseDate } from '../utils/dateUtils';

const EnrollmentModal = ({ 
  isOpen, 
  onClose, 
  course,
  onSuccess
}) => {
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [expireDate, setExpireDate] = useState('');
  const [showEnrolled, setShowEnrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { students, loading: usersLoading, getUserById, users: allUsers } = useCourseUsers();
  const { 
    enrolledStudents, 
    loading: enrollmentLoading,
    operationLoading,
    enrollStudents, 
    unenrollStudent,
    unenrollStudents,
    refreshEnrollments 
  } = useUserEnrollment(course?.id);

  useEffect(() => {
    if (isOpen) {
      // Set default expire date to 3 months from now
      const defaultExpire = new Date();
      defaultExpire.setMonth(defaultExpire.getMonth() + 3);
      setExpireDate(defaultExpire.toISOString().slice(0, 16));
      setSelectedStudents([]);
      setSearchQuery(''); // Reset search when modal opens
    }
  }, [isOpen]);

  const handleEnrollStudents = async () => {
    if (selectedStudents.length === 0) return;
    
    const result = await enrollStudents(selectedStudents, expireDate);
    
    if (result.success) {
      onSuccess?.(`Successfully enrolled ${result.enrolledCount} students!`);
      setSelectedStudents([]);
      onClose();
    }
  };

  const handleUnenrollStudent = async (studentId) => {
    const result = await unenrollStudent(studentId);
    
    if (result.success) {
      onSuccess?.('Student unenrolled successfully!');
    }
  };

  const handleBulkUnenroll = async () => {
    if (selectedStudents.length === 0) return;
    
    const result = await unenrollStudents(selectedStudents);
    
    if (result.success) {
      onSuccess?.(`Successfully unenrolled ${result.unenrolledCount} students!`);
      setSelectedStudents([]);
    }
  };

  const handleClose = () => {
    setSelectedStudents([]);
    setShowEnrolled(false);
    onClose();
  };

  // Get students not already enrolled
  // If no students found by role, use all users as potential students
  const potentialStudents = students.length > 0 ? students : allUsers;
  const availableStudents = potentialStudents.filter(student => 
    !enrolledStudents.some(enrolled => enrolled.userId === student.id)
  );

  // Filter available students based on search query
  const filteredAvailableStudents = availableStudents.filter(student => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    const name = (student.name || '').toLowerCase();
    const email = (student.email || '').toLowerCase();
    const role = Array.isArray(student.role) ? student.role.join(' ').toLowerCase() : (student.role || '').toLowerCase();
    
    return name.includes(query) || email.includes(query) || role.includes(query);
  });

  // Get currently enrolled student IDs for easy lookup
  const enrolledStudentIds = enrolledStudents.map(e => e.userId);

  // Filter enrolled students based on search query (for enrolled students tab)
  const filteredEnrolledStudents = enrolledStudents.filter(enrollment => {
    if (!searchQuery) return true;
    
    const student = getUserById(enrollment.userId);
    if (!student) return false;
    
    const query = searchQuery.toLowerCase();
    const name = (student.name || '').toLowerCase();
    const email = (student.email || '').toLowerCase();
    const role = Array.isArray(student.role) ? student.role.join(' ').toLowerCase() : (student.role || '').toLowerCase();
    
    return name.includes(query) || email.includes(query) || role.includes(query);
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between pb-3 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <UsersIcon className="h-5 w-5 mr-2 text-green-600" />
            Manage Course Enrollment
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Course Info */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                <span className="text-blue-600 font-semibold text-lg">
                  {course?.name?.charAt(0) || 'C'}
                </span>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-900">{course?.name}</h4>
                <p className="text-xs text-gray-500">
                  {course?.category || 'General'} • {course?.status || 'Draft'}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm font-medium text-gray-900">
                {enrolledStudents.length} Enrolled
              </div>
              <div className="text-xs text-gray-500">
                {availableStudents.length} Available
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mt-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setShowEnrolled(false)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                !showEnrolled
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Enroll Students ({availableStudents.length})
            </button>
            <button
              onClick={() => setShowEnrolled(true)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                showEnrolled
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Currently Enrolled ({enrolledStudents.length})
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="mt-6">
          {!showEnrolled ? (
            /* Enroll Students Tab */
            <div>
              {/* Expire Date Input */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <label className="block text-sm font-medium text-blue-900 mb-2">
                  <CalendarIcon className="h-4 w-4 inline mr-1" />
                  Course Expiration Date & Time
                </label>
                <input
                  type="datetime-local"
                  value={expireDate}
                  onChange={(e) => setExpireDate(e.target.value)}
                  className="w-full rounded-md border-blue-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                />
                <p className="text-xs text-blue-600 mt-1">
                  Students will lose access to this course after this date
                </p>
              </div>

              {/* Search Input */}
              <div className="relative mb-4">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search students by name, email, or role..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  disabled={usersLoading}
                />
              </div>

              {/* Student Selection */}
              <div className="mb-4 flex justify-between items-center">
                <label className="text-sm font-medium text-gray-700">
                  Select Students to Enroll ({selectedStudents.length} selected)
                  {searchQuery && (
                    <span className="text-xs text-gray-500 ml-2">
                      - Showing {filteredAvailableStudents.length} of {availableStudents.length} students
                    </span>
                  )}
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedStudents(filteredAvailableStudents.map(s => s.id))}
                    className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-md hover:bg-green-200"
                  >
                    Select All Visible ({filteredAvailableStudents.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedStudents([])}
                    className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                  >
                    Clear All
                  </button>
                </div>
              </div>

              {usersLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                  <p className="text-sm text-gray-500 mt-2">Loading students...</p>
                </div>
              ) : filteredAvailableStudents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <UsersIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  {searchQuery ? (
                    <div>
                      <p>No students found matching "{searchQuery}"</p>
                      <p className="text-xs text-gray-400 mt-1">Try adjusting your search terms</p>
                    </div>
                  ) : (
                    <p>
                      {allUsers.length === 0 
                        ? 'No users found in database' 
                        : 'All students are already enrolled in this course'
                      }
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto border rounded-lg p-2">
                  {filteredAvailableStudents.map((student) => (
                    <label key={student.id} className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedStudents.includes(student.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedStudents([...selectedStudents, student.id]);
                          } else {
                            setSelectedStudents(selectedStudents.filter(id => id !== student.id));
                          }
                        }}
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center ml-3 mr-3">
                        <UserIcon className="h-4 w-4 text-green-600" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{student.name}</div>
                        <div className="text-xs text-gray-500">
                          {student.email} • {student.role || 'Student'}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Currently Enrolled Tab */
            <div>
              {/* Search Input for Enrolled Students */}
              <div className="relative mb-4">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search enrolled students by name, email, or role..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={enrollmentLoading}
                />
              </div>

              <div className="mb-4 flex justify-between items-center">
                <label className="text-sm font-medium text-gray-700">
                  Currently Enrolled Students
                  {searchQuery && (
                    <span className="text-xs text-gray-500 ml-2">
                      - Showing {filteredEnrolledStudents.length} of {enrolledStudents.length} students
                    </span>
                  )}
                </label>
                {selectedStudents.length > 0 && (
                  <button
                    onClick={handleBulkUnenroll}
                    disabled={operationLoading}
                    className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded-md hover:bg-red-200 disabled:opacity-50"
                  >
                    Unenroll Selected ({selectedStudents.length})
                  </button>
                )}
              </div>

              {enrollmentLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="text-sm text-gray-500 mt-2">Loading enrollments...</p>
                </div>
              ) : filteredEnrolledStudents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <UsersIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  {searchQuery ? (
                    <div>
                      <p>No enrolled students found matching "{searchQuery}"</p>
                      <p className="text-xs text-gray-400 mt-1">Try adjusting your search terms</p>
                    </div>
                  ) : (
                    <p>No students are enrolled in this course yet</p>
                  )}
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto border rounded-lg p-2">
                  {filteredEnrolledStudents.map((enrollment) => {
                    const student = enrollment.userData;
                    
                    // Use the improved date parsing utility
                    const expiry = parseDate(enrollment.enrollmentData.expire_date);
                    const enrolledDate = parseDate(enrollment.enrollmentData.enrolled_at);
                    const isExpired = expiry ? expiry < new Date() : false;
                    
                    return (
                      <div key={enrollment.userId} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedStudents.includes(enrollment.userId)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedStudents([...selectedStudents, enrollment.userId]);
                              } else {
                                setSelectedStudents(selectedStudents.filter(id => id !== enrollment.userId));
                              }
                            }}
                            className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded mr-3"
                          />
                          <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                            <UserIcon className="h-4 w-4 text-blue-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{student.name}</div>
                            <div className="text-xs text-gray-500">
                              {student.email} • Enrolled: {enrolledDate ? enrolledDate.toLocaleDateString() : 'Unknown date'}
                            </div>
                            <div className={`text-xs flex items-center mt-1 ${isExpired ? 'text-red-600' : 'text-green-600'}`}>
                              <ClockIcon className="h-3 w-3 mr-1" />
                              {expiry ? (
                                <>
                                  Expires: {expiry.toLocaleDateString()} at {expiry.toLocaleTimeString()}
                                  {isExpired && <span className="ml-2 text-red-600 font-medium">(EXPIRED)</span>}
                                </>
                              ) : (
                                <span className="text-gray-500">No expiry date set</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleUnenrollStudent(enrollment.userId)}
                          disabled={operationLoading}
                          className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50 disabled:opacity-50"
                          title="Unenroll Student"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end pt-6 border-t border-gray-200 gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Close
          </button>
          {!showEnrolled && (
            <button
                          onClick={handleEnrollStudents}
            disabled={selectedStudents.length === 0 || !expireDate || operationLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {operationLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Enrolling...
                </>
              ) : (
                <>
                  <CheckIcon className="h-4 w-4 mr-2" />
                  Enroll {selectedStudents.length} Students
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnrollmentModal;
